import {
  isEmptyString,
} from '@populateui/utils';
import {
  FillableFormField,
  StableKeySource,
} from 'views/EMR/components/FillableForm/types';

export function getStableKey(
  item: StableKeySource,
  fallbackPrefix: string | null = 'item',
) {
  const prefix = fallbackPrefix ?? 'item';
  const { rect } = item;
  return item.id
    ?? item.aliasId
    ?? item.name
    ?? item.title
    ?? `${prefix}-${item.type ?? 'item'}-${item.pageNum ?? 'page'}-${rect ? `${rect.x ?? ''}-${rect.y ?? ''}-${rect.width ?? ''}-${rect.height ?? ''}` : ''}`;
}

export function getStyleFromRectForPage(
  field: FillableFormField,
  pageHeight: number,
  scaleFactor: number,
  isSignField?: boolean,
) {
  const { rect } = field;
  const left = (rect?.x ?? 0) * scaleFactor;
  const top = (pageHeight - (rect?.y ?? 0)) * scaleFactor;
  const width = (rect?.width ?? 0) * scaleFactor;
  const height = (rect?.height ?? 0) * scaleFactor;

  return {
    position: 'absolute' as const,
    top,
    left,
    width,
    height: height + (isSignField ? 1 : 0),
    padding: isSignField ? 0 : 2,
    backgroundColor: '#f7f8fc',
    border: '1px solid #bee7f3',
    color: '#527490',
    boxSizing: 'border-box' as const,
  };
}

export function getFieldVerticalAlignment(alignment?: string | null) {
  switch ((alignment ?? '').trim().toLowerCase()) {
    case 'bottom':
      return 'flex-end';
    case 'middle':
    case 'center':
      return 'center';
    default:
      return 'flex-start';
  }
}

export function getFieldHorizontalAlignment(alignment?: string | null) {
  switch ((alignment ?? '').trim().toLowerCase()) {
    case 'center':
    case 'middle':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'flex-start';
  }
}

export function getFieldTextAlignment(alignment?: string | null) {
  switch ((alignment ?? '').trim().toLowerCase()) {
    case 'center':
    case 'middle':
      return 'center';
    case 'right':
      return 'right';
    default:
      return 'left';
  }
}

export const placeholderByType: Partial<Record<string, string>> = Object.freeze({
  fullName: 'Please enter name',
  email: 'Please enter email (e.g: hello@example.com)',
  usAddress: 'Please enter address',
  number: 'Please enter a number (e.g: 123)',
  percent: 'Please enter percentage value',
  integer: 'Please enter a number (e.g: 123)',
  ein: 'Please enter EIN (e.g: 99-8888887)',
  ssn: 'Please enter SSN (e.g: 5555-44-3333)',
});

const SIGNATURE_FAMILY_FIELD_TYPES = new Set([
  'signature',
  'initial',
  'signatureDate',
  'signatureTimestamp',
]);

export type FormFieldRequiredSource = Pick<Partial<FillableFormField>, 'isRequired' | 'type'>;

export function isFormFieldStrictlyRequired(
  field?: FormFieldRequiredSource,
): boolean {
  return SIGNATURE_FAMILY_FIELD_TYPES.has(field?.type ?? '')
    || field?.isRequired !== false;
}

export function getFieldDisplayLabel(
  field?: { name?: string | null; type?: string | null },
): string | undefined {
  if (field?.type === 'checkbox') {
    return undefined;
  }
  if (isEmptyString(field?.name)) {
    return field?.type === 'radioGroup' || field?.type === 'checkboxGroup'
      ? 'Please Select One'
      : undefined;
  }
  return field?.name ?? undefined;
}
